import type { PlacementPosition, SizeMm } from '@repo/api-contracts';
import { roundMm } from './drawn-shape';

/** 표면 mm 사각형. 왼쪽 위 좌표와 크기. */
export interface Rect {
  readonly xMm: number;
  readonly yMm: number;
  readonly widthMm: number;
  readonly heightMm: number;
}

export type ResizeHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/** 끌어서 그린 사각형의 최소 변 길이(mm). */
export const MIN_DRAW_MM = 5;

const clamp = (value: number, max: number): number => Math.min(Math.max(value, 0), max);
/** 1mm 로 반올림한 뒤 표면 안으로 다시 맞춘다. 표면 끝까지 끈 값은 끝이 소수여도 끝 값을 유지한다. */
const snap = (value: number, max: number): number =>
  value >= max ? max : clamp(Math.round(value), max);

/** 가장자리 → 사각형. 표면 끝 값이 소수면 뺄셈이 찌꺼기(62.527951200000004)를 남기므로 1e-7mm 로 지운다. */
const rectOfEdges = (left: number, top: number, right: number, bottom: number): Rect => ({
  xMm: roundMm(left),
  yMm: roundMm(top),
  widthMm: roundMm(right - left),
  heightMm: roundMm(bottom - top),
});

/** 두 표면 mm 점을 대각 모서리로 하는 사각형. 끌기 방향과 무관하고, square 면 짧은 변에 맞춘 정사각형이다. */
export const rectFromSurfacePoints = (
  realSize: SizeMm,
  a: PlacementPosition,
  b: PlacementPosition,
  options: { square: boolean },
):
  | { ok: true; rect: Rect }
  | {
      ok: false;
      reason: string;
    } => {
  const ax = clamp(a.xMm, realSize.widthMm);
  const ay = clamp(a.yMm, realSize.heightMm);
  let bx = clamp(b.xMm, realSize.widthMm);
  let by = clamp(b.yMm, realSize.heightMm);
  if (options.square) {
    const side = Math.min(Math.abs(bx - ax), Math.abs(by - ay));
    bx = ax + (bx >= ax ? side : -side);
    by = ay + (by >= ay ? side : -side);
  }
  const left = snap(Math.min(ax, bx), realSize.widthMm);
  const right = snap(Math.max(ax, bx), realSize.widthMm);
  const top = snap(Math.min(ay, by), realSize.heightMm);
  const bottom = snap(Math.max(ay, by), realSize.heightMm);
  const rect = rectOfEdges(left, top, right, bottom);
  if (rect.widthMm < MIN_DRAW_MM || rect.heightMm < MIN_DRAW_MM)
    return { ok: false, reason: '5mm 이상 끌어서 그려 주세요.' };
  return { ok: true, rect };
};

/** 손잡이로 가장자리를 `to` 까지 옮긴 사각형. 반대쪽 가장자리는 고정하고 최소 크기 밑으로는 줄지 않는다. */
export const resizeRectByHandle = (
  realSize: SizeMm,
  start: Rect,
  handle: ResizeHandle,
  to: PlacementPosition,
  options: { keepRatio: boolean },
): Rect => {
  let { xMm: left, yMm: top } = start;
  let right = start.xMm + start.widthMm;
  let bottom = start.yMm + start.heightMm;
  const movesW = handle.includes('w');
  const movesE = handle.includes('e');
  const movesN = handle.includes('n');
  const movesS = handle.includes('s');
  let target = {
    xMm: clamp(to.xMm, realSize.widthMm),
    yMm: clamp(to.yMm, realSize.heightMm),
  };

  if (options.keepRatio && handle.length === 2) {
    const fixedX = movesW ? right : left;
    const fixedY = movesN ? bottom : top;
    const dirX = movesW ? -1 : 1;
    const dirY = movesN ? -1 : 1;
    const roomX = dirX > 0 ? realSize.widthMm - fixedX : fixedX;
    const roomY = dirY > 0 ? realSize.heightMm - fixedY : fixedY;
    const wanted = Math.max(
      Math.abs(target.xMm - fixedX) / start.widthMm,
      Math.abs(target.yMm - fixedY) / start.heightMm,
    );
    const smallest = MIN_DRAW_MM / Math.min(start.widthMm, start.heightMm);
    const largest = Math.min(roomX / start.widthMm, roomY / start.heightMm);
    const scale = Math.max(smallest, Math.min(wanted, largest));
    target = {
      xMm: fixedX + dirX * start.widthMm * scale,
      yMm: fixedY + dirY * start.heightMm * scale,
    };
  }

  if (movesW) left = snap(target.xMm, realSize.widthMm);
  if (movesE) right = snap(target.xMm, realSize.widthMm);
  if (movesN) top = snap(target.yMm, realSize.heightMm);
  if (movesS) bottom = snap(target.yMm, realSize.heightMm);
  if (right - left < MIN_DRAW_MM) {
    if (movesW) left = right - MIN_DRAW_MM;
    else right = left + MIN_DRAW_MM;
  }
  if (bottom - top < MIN_DRAW_MM) {
    if (movesN) top = bottom - MIN_DRAW_MM;
    else bottom = top + MIN_DRAW_MM;
  }
  return rectOfEdges(left, top, right, bottom);
};
